
import React, { createContext, useContext, useState, ReactNode } from 'react';
import { GuestData, EventData, SelectedDate, TimeSlot } from '../types';
import { RSVPFlowState } from './types';
import { useRSVPStateMachine } from '../hooks/useRSVPStateMachine';
import { useRSVPSubmission } from '../hooks/useRSVPSubmission';

interface RSVPContextType {
  // State
  eventId: string | null;
  currentState: RSVPFlowState;
  selectedGuest: GuestData | null;
  selectedDates: SelectedDate[];
  selectedTimes: TimeSlot[];
  availableGuests: GuestData[];
  respondedGuests: GuestData[];
  inProgressGuests: GuestData[];
  phoneNumber: string;
  verificationCode: string;
  feedback: string;
  rsvpResponse: 'yes' | 'no' | null;
  guestAvatarUrl: string | null;
  eventData: EventData | null;
  isLoading: boolean;
  isUpdate: boolean;
  error: Error | null;
  isSubmitting: boolean;
  submitError: Error | null;
  
  // Actions
  setEventId: (id: string) => void;
  goToNextStep: () => void;
  goToPreviousStep: () => void;
  goToSpecificStep: (step: RSVPFlowState) => void;
  setSelectedGuest: (guest: GuestData | null) => void;
  setSelectedDates: (dates: SelectedDate[]) => void;
  addSelectedDate: (date: SelectedDate) => void;
  removeSelectedDate: (date: SelectedDate) => void;
  setSelectedTimes: (times: TimeSlot[]) => void;
  addSelectedTime: (time: TimeSlot) => void;
  removeSelectedTime: (time: TimeSlot) => void;
  setAvailableGuests: (guests: GuestData[]) => void;
  setRespondedGuests: (guests: GuestData[]) => void;
  setInProgressGuests: (guests: GuestData[]) => void;
  setPhoneNumber: (phone: string) => void;
  setVerificationCode: (code: string) => void;
  setFeedback: (text: string) => void;
  setRsvpResponse: (response: 'yes' | 'no' | null) => void;
  setGuestAvatarUrl: (url: string | null) => void;
  setEventData: (data: EventData) => void;
  submitRSVP: () => Promise<boolean>;
  resetSubmitState: () => void;
  setIsUpdate: (isUpdate: boolean) => void;
}

const RSVPContext = createContext<RSVPContextType | undefined>(undefined);

interface RSVPProviderProps {
  children: ReactNode;
  eventId: string;
  isUpdate?: boolean;
  initialState?: RSVPFlowState;
}

export const RSVPProvider: React.FC<RSVPProviderProps> = ({ 
  children, 
  eventId,
  isUpdate = false,
  initialState
}) => {
  // Event and guest data
  const [eventIdState, setEventIdState] = useState<string | null>(eventId);
  const [eventData, setEventData] = useState<EventData | null>(null);
  const [availableGuests, setAvailableGuests] = useState<GuestData[]>([]);
  const [respondedGuests, setRespondedGuests] = useState<GuestData[]>([]);
  const [inProgressGuests, setInProgressGuests] = useState<GuestData[]>([]);
  const [isUpdateState, setIsUpdate] = useState(isUpdate);
  
  // User selections
  const [selectedGuest, setSelectedGuest] = useState<GuestData | null>(null);
  const [selectedDates, setSelectedDates] = useState<SelectedDate[]>([]);
  const [selectedTimes, setSelectedTimes] = useState<TimeSlot[]>([]);
  const [phoneNumber, setPhoneNumber] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const [feedback, setFeedback] = useState('');
  const [rsvpResponse, setRsvpResponse] = useState<'yes' | 'no' | null>(null);
  const [guestAvatarUrl, setGuestAvatarUrl] = useState<string | null>(null);
  
  // State machine - fixed by passing initialState directly
  const { 
    currentState, 
    goToNextStep, 
    goToPreviousStep,
    transitionTo,
  } = useRSVPStateMachine(initialState);
  
  // Add missing goToSpecificStep function
  const goToSpecificStep = (step: RSVPFlowState) => {
    transitionTo(step);
  };
  
  // Add missing isLoading and error for state machine
  const isLoading = false;
  const error = null;
  
  // RSVP submission
  const {
    submitRSVP,
    isSubmitting,
    error: submitError,
  } = useRSVPSubmission({
    eventId: eventIdState,
    selectedGuest,
    selectedDates,
    selectedTimes,
    phoneNumber,
    feedback,
    rsvpResponse,
    guestAvatarUrl,
    isUpdate: isUpdateState
  });
  
  // Add missing resetSubmitState function
  const resetSubmitState = () => {
    // This would reset submission state if needed
  };
  
  // Helper functions for selected dates
  const addSelectedDate = (date: SelectedDate) => {
    if (!selectedDates.includes(date)) {
      setSelectedDates([...selectedDates, date]);
    }
  };
  
  const removeSelectedDate = (date: SelectedDate) => {
    setSelectedDates(selectedDates.filter(d => d !== date));
  };
  
  // Helper functions for selected times
  const addSelectedTime = (time: TimeSlot) => {
    if (!selectedTimes.includes(time)) {
      setSelectedTimes([...selectedTimes, time]);
    }
  };
  
  const removeSelectedTime = (time: TimeSlot) => {
    setSelectedTimes(selectedTimes.filter(t => t !== time));
  };
  
  const setEventId = (id: string) => {
    setEventIdState(id);
  };
  
  const value: RSVPContextType = {
    // State
    eventId: eventIdState,
    currentState,
    selectedGuest,
    selectedDates,
    selectedTimes,
    availableGuests,
    respondedGuests,
    inProgressGuests,
    phoneNumber,
    verificationCode,
    feedback,
    rsvpResponse,
    guestAvatarUrl,
    eventData,
    isLoading,
    error,
    isSubmitting,
    submitError,
    isUpdate: isUpdateState,
    
    // Actions
    setEventId,
    goToNextStep,
    goToPreviousStep,
    goToSpecificStep,
    setSelectedGuest,
    setSelectedDates,
    addSelectedDate,
    removeSelectedDate,
    setSelectedTimes,
    addSelectedTime,
    removeSelectedTime,
    setAvailableGuests,
    setRespondedGuests,
    setInProgressGuests,
    setPhoneNumber,
    setVerificationCode,
    setFeedback,
    setRsvpResponse,
    setGuestAvatarUrl,
    setEventData,
    submitRSVP,
    resetSubmitState,
    setIsUpdate
  };
  
  return (
    <RSVPContext.Provider value={value}>
      {children}
    </RSVPContext.Provider>
  );
};

export const useRSVP = () => {
  const context = useContext(RSVPContext);
  if (context === undefined) {
    throw new Error('useRSVP must be used within an RSVPProvider');
  }
  return context;
};
